import { ApolloProvider } from '@apollo/client';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import './index.css';
import { apolloClient } from './api/graphql/client';
import { router } from './router/router';

import * as Sentry from '@sentry/react';
import TagManager from 'react-gtm-module';

import LogRocket from 'logrocket';
import { Alert, ThemeProvider } from '@mui/material';
import { createTheme } from './new/themes';
import auth, { type User } from './components/auth';
import { Agentation } from 'agentation';
import {
  getSessionId,
  registerSessionSubscribers,
} from '@services/session-tracker/sessionTracker';
import { Typography } from '@design-system/components/typography/typography';
import { identifySentry, initSentry } from './sentry';
import { initializeCustomerIO } from './new/services/customerio';

if (import.meta.env.VITE_ENV === 'production') {
  const tagManagers = [
    {
      gtmId: 'GTM-TWWN56K',
    },
    // requested by Amir
    {
      gtmId: 'GTM-MHPTLC4K',
    },
  ];

  tagManagers.forEach((tagManager) => TagManager.initialize(tagManager));
}

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
if (import.meta.env.VITE_ENV === 'production' && posthogKey) {
  // Analytics is optional — a failed chunk fetch (stale deploy, flaky mobile
  // network) must not surface as an unhandled rejection in Sentry.
  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: 'https://us.i.posthog.com',
        autocapture: true,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: false,
        mask_all_element_attributes: true,
        mask_all_text: true,
        mask_personal_data_properties: true,
        session_recording: {
          maskAllInputs: true,
        },
      });
    })
    .catch(() => {
      // Ignore: analytics chunk failed to load.
    });
}

initSentry();

// Initialize Customer.io
const customerIOSiteId = import.meta.env.VITE_CUSTOMERIO_SITE_ID;
if (customerIOSiteId) {
  initializeCustomerIO(customerIOSiteId);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).data = {
  environment: import.meta.env.VITE_ENV || 'development',
};

const showDevtools = import.meta.env.VITE_ENV === 'development';

const fallbackRender: Sentry.FallbackRender = ({ error }) => {
  const user = auth.getUser();
  // Call resetErrorBoundary() to reset the error boundary and retry the render.
  return (
    <div
      role="alert"
      className="flex justify-center w-screen h-screen bg-white max-w-screen mt-[20vh]"
    >
      <div>
        <Typography variant="headline" size="large" className="mb-10">
          Unexpected error happened
        </Typography>
        <Typography variant="bodyLarge" className="mb-10">
          We are sorry for inconvenience. <br />
          Please contact our{' '}
          <a
            href="mailto:creatorsupport@playbookapp.io"
            className="text-black underline"
          >
            support team
          </a>{' '}
          and provide this session id: <b>{getSessionId()}</b>
          <br />
          We will try to resolve this issue quickly.
        </Typography>
        <Alert severity="error">
          <div style={{ color: 'red' }} className="whitespace-pre-wrap">
            ERROR {user ? user.influencerId : '(no user)'} at{' '}
            {window.location.pathname}:{' '}
            {error instanceof Error ? error.message : String(error)}
          </div>
        </Alert>
      </div>
    </div>
  );
};

const App = () => {
  const user = getUserFromLS();
  useEffect(registerSessionSubscribers, []);
  useEffect(() => {
    identifyLogRocket(user);
  }, [user]);
  useEffect(() => {
    identifySentry(user);
  }, [user]);
  useEffect(() => {
    const handler = () => {
      const user = getUserFromLS();
      identifySentry(user);
      identifyLogRocket(user);
    };
    window.addEventListener('User.login()', handler);

    return () => {
      window.removeEventListener('User.login()', handler);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={fallbackRender}>
      <ThemeProvider theme={createTheme()}>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            {showDevtools && <ReactQueryDevtools initialIsOpen={false} />}
            <RouterProvider router={router} />
            {(import.meta.env.DEV ||
              import.meta.env.VITE_ENV === 'development') && <Agentation />}
          </QueryClientProvider>
        </ApolloProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
};

const container = document.getElementById('app');
if (!container) throw new Error('Missing #app mount element in index.html');
createRoot(container).render(<App />);

function getUserFromLS(): User | null {
  try {
    const storedUser = window.localStorage.getItem('user');
    return storedUser === null ? null : JSON.parse(storedUser);
  } catch (e) {
    console.error("Couldn't parse user from local storage", e);
    return null;
  }
}

function identifyLogRocket(user: User | null) {
  if (import.meta.env.VITE_ENV !== 'production') return;

  if (user && user.influencer) {
    LogRocket.init('9oooat/creator-admin-panel');

    const creatorName = user.influencer
      ? user.influencer.firstName + user.influencer.lastName
      : '';

    LogRocket.identify(String(user.id), {
      name:
        import.meta.env.VITE_ENV +
        ' | ' +
        user.firstName +
        ' ' +
        user.lastName +
        (creatorName ? ' (' + creatorName + ')' : ''),
      email: user.email,
      influencerId: user.influencerId,
      influencer: JSON.stringify(user.influencer),
      role: user.role,
    });
    console.info('Identified creator', user);
  }
}
